// Software Licence detail -- purchase/financial info, seat utilisation,
// current assignments (Unassign), full assignment history, Assign, Edit,
// Archive. Another unit's licence comes back 404 from the backend.
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaEdit, FaArchive, FaUserPlus } from "react-icons/fa";
import api from "../../api";
import { usePermissions } from "../../context/PermissionContext";
import { PageHeader, Card, Button, DataTable, ErrorState, LoadingState, Modal } from "../../components/ui";
import LicenseFormModal from "./LicenseFormModal";
import { LicenseStatusBadge, typeLabel, money, fmtDate, licenceBasePath, apiError } from "./licenseFormat";
import "./SoftwareLicenses.css";

const todayIst = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());

function Info({ label, children }) {
  return (
    <div className="sl-info">
      <span>{label}</span>
      <strong>{children ?? "-"}</strong>
    </div>
  );
}

function assigneeLabel(a) {
  const parts = [];
  if (a.assigned_user_id) parts.push(`${a.assigned_user_name || `User #${a.assigned_user_id}`}${a.employee_code ? ` (${a.employee_code})` : ""}`);
  if (a.assigned_asset_id) parts.push(`${a.assigned_asset_name || "Asset"} · ${a.assigned_asset_sku || `#${a.assigned_asset_id}`}${a.assigned_asset_serial ? ` · SN ${a.assigned_asset_serial}` : ""}`);
  return parts;
}

const describeCandidate = (kind, o) => (kind === "users"
  ? `${o.name || o.username}${o.employee_code ? ` (${o.employee_code})` : ""}${o.email ? ` — ${o.email}` : ""}`
  : `${o.name} · ${o.sku}${o.serial_no ? ` · SN ${o.serial_no}` : ""}`);

// Search-as-you-type multi-select over the licence's own unit (backend-scoped).
// `selected` is { [id]: { ...option, seats } } so picks survive a change of
// search text; one pick can take several seats (up to `maxSeats`).
function CandidatePicker({ label, licenceId, kind, selected, onChange, maxSeats }) {
  const [q, setQ] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/software-licenses/${licenceId}/assignable-${kind}`, { params: { q: q.trim() } })
        .then((res) => { setOptions(res.data?.data || []); setFailed(false); })
        .catch(() => { setOptions([]); setFailed(true); })
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, licenceId, kind]);

  const toggle = (o) => {
    const next = { ...selected };
    if (next[o.id]) delete next[o.id];
    else next[o.id] = { ...o, seats: 1 };
    onChange(next);
  };
  const setSeats = (o, seats) => onChange({ ...selected, [o.id]: { ...o, seats } });
  const picked = Object.values(selected);

  return (
    <div className="sl-field sl-span-2">
      <span className="form-label">{label}{picked.length > 0 && ` — ${picked.length} selected`}</span>
      {picked.length > 0 && (
        <div className="sl-picked">
          {picked.map((o) => (
            <div key={o.id} className="sl-picked-row">
              <span className="sl-picked-name">{describeCandidate(kind, o)}</span>
              <label className="sl-picked-seats">
                Seats
                <input
                  type="number"
                  className="form-control form-control-sm"
                  min={1}
                  max={maxSeats}
                  value={o.seats}
                  onChange={(e) => setSeats(o, e.target.value === "" ? "" : Math.max(1, Math.floor(Number(e.target.value)) || 1))}
                />
              </label>
              <button type="button" className="sl-link" onClick={() => setSeats(o, maxSeats)} title="Give this one every remaining seat">All</button>
              <button type="button" className="sl-picked-remove" aria-label="Remove" onClick={() => toggle(o)}>×</button>
            </div>
          ))}
        </div>
      )}
      <input
        className="form-control mb-1"
        placeholder={kind === "users" ? "Search name, username, email or employee code…" : "Search name, SKU or serial…"}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <div className="sl-pick-list" role="listbox" aria-multiselectable="true">
        {loading && options.length === 0 ? (
          <div className="sl-pick-empty">Loading…</div>
        ) : failed ? (
          <div className="sl-pick-empty text-danger">Could not load the list.</div>
        ) : options.length === 0 ? (
          <div className="sl-pick-empty">No match{q ? ` for "${q}"` : ""}.</div>
        ) : (
          options.map((o) => (
            <label key={o.id} className="sl-pick-item">
              <input type="checkbox" checked={!!selected[o.id]} onChange={() => toggle(o)} />
              <span>{describeCandidate(kind, o)}</span>
              {o.held_seats > 0 && <small className="text-muted ms-auto text-nowrap">Holds {o.held_seats} seat(s)</small>}
            </label>
          ))
        )}
      </div>
    </div>
  );
}

export default function SoftwareLicenseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = licenceBasePath(location.pathname);
  const { can } = usePermissions();

  const [licence, setLicence] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [showEdit, setShowEdit] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const emptyAssignForm = () => ({ users: {}, assets: {}, assigned_date: todayIst(), remarks: "" });
  const [assignForm, setAssignForm] = useState(emptyAssignForm);
  const [unassignTarget, setUnassignTarget] = useState(null);
  const [unassignForm, setUnassignForm] = useState({ unassigned_date: todayIst(), remarks: "" });
  const [showArchive, setShowArchive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    api.get(`/software-licenses/${id}`)
      .then((res) => setLicence(res.data?.data || null))
      .catch((err) => setError(err?.response?.status === 404 ? "Software licence not found." : apiError(err, "Failed to load the licence.")))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(""), 4000);
    return () => clearTimeout(t);
  }, [notice]);
  useEffect(() => {
    api.get("/software-licenses/meta").then((res) => setMeta(res.data?.data || null)).catch(() => setMeta(null));
  }, []);

  const run = async (fn, okMessage, close) => {
    setBusy(true);
    setActionError("");
    try {
      const res = await fn();
      if (res?.data?.data) setLicence(res.data.data);
      close();
      setNotice(okMessage);
    } catch (err) {
      setActionError(apiError(err, "Action failed."));
    } finally {
      setBusy(false);
    }
  };

  if (loading && !licence) return <LoadingState message="Loading licence…" />;
  if (error) {
    return (
      <ErrorState
        message={error}
        action={<Button variant="secondary" icon={<FaArrowLeft />} onClick={() => navigate(basePath)}>Back to Software Licences</Button>}
      />
    );
  }
  if (!licence) return null;

  const archived = licence.status === "ARCHIVED";
  const assignable = licence.effective_status === "ACTIVE" && licence.available_seats > 0;
  const needsUser = licence.license_type === "PER_USER";
  const needsAsset = licence.license_type === "PER_DEVICE";
  const usedPct = licence.total_seats ? Math.round((licence.assigned_seats / licence.total_seats) * 100) : 0;

  // Each selected user / asset takes the number of seats entered against it.
  // A Per User licence only offers users, a Per Device licence only assets.
  const submitAssign = async () => {
    const users = needsAsset ? [] : Object.values(assignForm.users);
    const assets = needsUser ? [] : Object.values(assignForm.assets);
    const targets = [
      ...users.map((o) => ({ kind: "users", o, body: { assigned_user_id: Number(o.id), seats: Number(o.seats) || 1 } })),
      ...assets.map((o) => ({ kind: "assets", o, body: { assigned_asset_id: Number(o.id), seats: Number(o.seats) || 1 } })),
    ];
    if (targets.length === 0) {
      return setActionError(needsUser ? "Select at least one user." : needsAsset ? "Select at least one asset / device." : "Select at least one user or asset / device.");
    }
    const seatsWanted = targets.reduce((n, t) => n + t.body.seats, 0);
    if (seatsWanted > licence.available_seats) {
      return setActionError(`${seatsWanted} seat(s) selected, but only ${licence.available_seats} are available.`);
    }

    setBusy(true);
    setActionError("");
    const failed = [];
    let latest = null;
    // Sequential so the backend's seat-count lock sees each insert in turn.
    for (const t of targets) {
      try {
        const res = await api.post(`/software-licenses/${licence.id}/assignments`, {
          ...t.body,
          assigned_date: assignForm.assigned_date,
          remarks: assignForm.remarks || null,
        });
        if (res?.data?.data) latest = res.data.data;
      } catch (err) {
        failed.push({ ...t, message: apiError(err, "Failed.") });
      }
    }
    if (latest) setLicence(latest);
    setBusy(false);

    const done = targets.length - failed.length;
    if (failed.length === 0) {
      setShowAssign(false);
      setNotice(seatsWanted === 1 ? "Licence assigned." : `${seatsWanted} seat(s) assigned.`);
      return undefined;
    }
    // Keep only the failures selected so a retry does not double-assign.
    const keep = (kind) => Object.fromEntries(failed.filter((f) => f.kind === kind).map((f) => [f.o.id, f.o]));
    setAssignForm((f) => ({ ...f, users: keep("users"), assets: keep("assets") }));
    if (done > 0) setNotice(`Licence assigned to ${done} of ${targets.length}.`);
    setActionError(failed.map((f) => `${describeCandidate(f.kind, f.o)}: ${f.message}`).join("\n"));
    return undefined;
  };
  const selectedCount = [
    ...(needsAsset ? [] : Object.values(assignForm.users)),
    ...(needsUser ? [] : Object.values(assignForm.assets)),
  ].reduce((n, o) => n + (Number(o.seats) || 1), 0);

  return (
    <div className="sl-page">
      <PageHeader
        eyebrow={<button type="button" className="sl-link" onClick={() => navigate(basePath)}><FaArrowLeft /> Software Licences</button>}
        title={licence.software_name}
        description={`${typeLabel(licence.license_type)} · ${licence.unit_name || "-"}${licence.vendor_name ? ` · ${licence.vendor_name}` : ""}`}
        actions={!archived && (
          <>
            {can("software_licenses", "assign") && (
              <Button
                icon={<FaUserPlus />}
                disabled={!assignable}
                title={assignable ? undefined : licence.effective_status !== "ACTIVE" ? "Only an active licence can be assigned" : "No seats available"}
                onClick={() => { setActionError(""); setAssignForm(emptyAssignForm()); setShowAssign(true); }}
              >
                Assign Licence
              </Button>
            )}
            {can("software_licenses", "update") && <Button variant="secondary" icon={<FaEdit />} onClick={() => setShowEdit(true)}>Edit</Button>}
            {can("software_licenses", "archive") && <Button variant="danger" icon={<FaArchive />} onClick={() => { setActionError(""); setShowArchive(true); }}>Archive</Button>}
          </>
        )}
      />

      {notice && <div className="alert alert-success py-2" role="status">{notice}</div>}

      <div className="sl-detail-grid">
        <Card title="Software & Status">
          <div className="sl-info-grid">
            <Info label="Status"><LicenseStatusBadge licence={licence} /></Info>
            <Info label="Licence Type">{typeLabel(licence.license_type)}</Info>
            <Info label="Vendor / Company">{licence.vendor_name || "-"}</Info>
            <Info label="Unit">{licence.unit_name || "-"}</Info>
            <Info label="Licence Key / Reference">{licence.license_key_masked || "-"}</Info>
            <Info label="Remarks">{licence.remarks || "-"}</Info>
          </div>
        </Card>

        <Card title="Purchase & Expiry">
          <div className="sl-info-grid">
            <Info label="Purchase Date">{fmtDate(licence.purchase_date)}</Info>
            <Info label="Invoice / Purchase Ref">{licence.purchase_reference || "-"}</Info>
            <Info label="Start Date">{fmtDate(licence.start_date)}</Info>
            <Info label="Expiry Date">
              {licence.license_type === "PERPETUAL" && !licence.expiry_date ? "Perpetual (no expiry)" : fmtDate(licence.expiry_date)}
            </Info>
            <Info label="Expiry">
              {licence.license_type === "PERPETUAL"
                ? "Never expires"
                : licence.days_to_expiry == null
                  ? "-"
                  : licence.days_to_expiry < 0
                    ? `Expired ${Math.abs(licence.days_to_expiry)} day(s) ago`
                    : licence.days_to_expiry === 0 ? "Expires today" : `${licence.days_to_expiry} day(s) left`}
            </Info>
          </div>
        </Card>

        <Card title="Financial">
          <div className="sl-info-grid">
            <Info label="Purchase Amount (historical)">{money(licence.purchase_amount)}</Info>
            <Info label="Active Licence Value"><span className="sl-good">{money(licence.active_value)}</span></Info>
            <Info label="Expired Licence Value"><span className="sl-bad">{money(licence.expired_value)}</span></Info>
          </div>
          <small className="text-muted">The purchase amount is never reduced; it counts as active value only while the licence is active.</small>
        </Card>

        <Card title="Seat Utilisation">
          <div className="sl-info-grid">
            <Info label="Total Seats">{licence.total_seats}</Info>
            <Info label="Assigned Seats">{licence.assigned_seats}</Info>
            <Info label="Available Seats">{licence.available_seats}</Info>
          </div>
          <div className="sl-meter" role="progressbar" aria-valuenow={usedPct} aria-valuemin={0} aria-valuemax={100} aria-label="Seats used">
            <span style={{ width: `${Math.min(usedPct, 100)}%` }} />
          </div>
          <small className="text-muted">{usedPct}% used</small>
        </Card>
      </div>

      <Card title={`Current Assignments (${licence.current_assignments.length})`} flush>
        <DataTable
          columns={[
            { key: "who", label: "Assigned To", minWidth: 220 },
            { key: "date", label: "Assigned Date" },
            { key: "by", label: "Assigned By" },
            { key: "remarks", label: "Remarks" },
            { key: "act", label: "", align: "center" },
          ]}
          loading={false}
          empty={licence.current_assignments.length === 0}
          emptyMessage="No seat of this licence is assigned."
        >
          {licence.current_assignments.map((a) => (
            <tr key={a.id}>
              <td>{assigneeLabel(a).map((p) => <div key={p}>{p}</div>)}</td>
              <td>{fmtDate(a.assigned_date)}</td>
              <td>{a.assigned_by_name || "-"}</td>
              <td>{a.remarks || "-"}</td>
              <td className="ui-td-center">
                {can("software_licenses", "assign") && (
                  <Button size="sm" variant="ghost" onClick={() => { setActionError(""); setUnassignForm({ unassigned_date: todayIst(), remarks: "" }); setUnassignTarget(a); }}>
                    Unassign
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </DataTable>
      </Card>

      <Card title="Assignment History" flush>
        <DataTable
          columns={[
            { key: "who", label: "Assigned To", minWidth: 220 },
            { key: "from", label: "Assigned" },
            { key: "to", label: "Unassigned" },
            { key: "status", label: "Status" },
            { key: "by", label: "By" },
            { key: "remarks", label: "Remarks" },
          ]}
          loading={false}
          empty={licence.assignment_history.length === 0}
          emptyMessage="No assignment history yet."
        >
          {licence.assignment_history.map((a) => (
            <tr key={a.id}>
              <td>{assigneeLabel(a).map((p) => <div key={p}>{p}</div>)}</td>
              <td>{fmtDate(a.assigned_date)}</td>
              <td>{fmtDate(a.unassigned_date)}</td>
              <td><span className={`ui-badge ui-badge-${a.status === "ASSIGNED" ? "green" : "gray"}`}>{a.status === "ASSIGNED" ? "Assigned" : "Unassigned"}</span></td>
              <td>{a.status === "ASSIGNED" ? a.assigned_by_name || "-" : a.unassigned_by_name || "-"}</td>
              <td>{[a.remarks, a.unassign_remarks].filter(Boolean).join(" / ") || "-"}</td>
            </tr>
          ))}
        </DataTable>
      </Card>

      <LicenseFormModal
        show={showEdit}
        licence={licence}
        meta={meta}
        onClose={() => setShowEdit(false)}
        onSaved={(saved) => { setShowEdit(false); if (saved) setLicence(saved); setNotice("Licence updated."); }}
      />

      <Modal
        show={showAssign}
        onClose={() => !busy && setShowAssign(false)}
        title="Assign Licence"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowAssign(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submitAssign} loading={busy} disabled={selectedCount === 0}>
              {selectedCount > 1 ? `Assign (${selectedCount})` : "Assign"}
            </Button>
          </>
        }
      >
        {actionError && <div className="alert alert-danger py-2" style={{ whiteSpace: "pre-line" }}>{actionError}</div>}
        <p className="text-muted small mb-2">
          {licence.available_seats} of {licence.total_seats} seat(s) available. Only users and assets of {licence.unit_name || "this unit"} can be selected.
          {" "}Set Seats to give one {needsAsset ? "asset" : "person"} more than one seat.
        </p>
        <div className="sl-form-grid">
          {!needsAsset && (
            <CandidatePicker
              label={`User / Employee${needsUser ? " *" : ""}`}
              licenceId={licence.id}
              kind="users"
              selected={assignForm.users}
              maxSeats={licence.available_seats}
              onChange={(v) => setAssignForm((f) => ({ ...f, users: v }))}
            />
          )}
          {!needsUser && (
            <CandidatePicker
              label={`Asset / Device${needsAsset ? " *" : ""}`}
              licenceId={licence.id}
              kind="assets"
              selected={assignForm.assets}
              maxSeats={licence.available_seats}
              onChange={(v) => setAssignForm((f) => ({ ...f, assets: v }))}
            />
          )}
          <label className="sl-field">
            <span className="form-label">Assigned Date</span>
            <input type="date" className="form-control" value={assignForm.assigned_date} onChange={(e) => setAssignForm((f) => ({ ...f, assigned_date: e.target.value }))} />
          </label>
          <label className="sl-field sl-span-2">
            <span className="form-label">Remarks</span>
            <input className="form-control" value={assignForm.remarks} onChange={(e) => setAssignForm((f) => ({ ...f, remarks: e.target.value }))} maxLength={1000} />
          </label>
        </div>
      </Modal>

      <Modal
        show={!!unassignTarget}
        onClose={() => !busy && setUnassignTarget(null)}
        size="sm"
        title="Unassign Licence"
        footer={
          <>
            <Button variant="secondary" onClick={() => setUnassignTarget(null)} disabled={busy}>Cancel</Button>
            <Button
              variant="danger"
              loading={busy}
              onClick={() => run(
                () => api.put(`/software-licenses/${licence.id}/assignments/${unassignTarget.id}/unassign`, unassignForm),
                "Licence unassigned.",
                () => setUnassignTarget(null)
              )}
            >
              Unassign
            </Button>
          </>
        }
      >
        {actionError && <div className="alert alert-danger py-2">{actionError}</div>}
        {unassignTarget && <p className="mb-2">{assigneeLabel(unassignTarget).join(" · ")}</p>}
        <label className="sl-field">
          <span className="form-label">Unassigned Date</span>
          <input type="date" className="form-control" value={unassignForm.unassigned_date} onChange={(e) => setUnassignForm((f) => ({ ...f, unassigned_date: e.target.value }))} />
        </label>
        <label className="sl-field mt-2">
          <span className="form-label">Remarks</span>
          <input className="form-control" value={unassignForm.remarks} onChange={(e) => setUnassignForm((f) => ({ ...f, remarks: e.target.value }))} maxLength={1000} />
        </label>
        <small className="text-muted d-block mt-2">The assignment stays in the history.</small>
      </Modal>

      <Modal
        show={showArchive}
        onClose={() => !busy && setShowArchive(false)}
        size="sm"
        title="Archive Licence"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowArchive(false)} disabled={busy}>Cancel</Button>
            <Button variant="danger" loading={busy} onClick={() => run(() => api.put(`/software-licenses/${licence.id}/archive`, {}), "Licence archived.", () => setShowArchive(false))}>
              Archive
            </Button>
          </>
        }
      >
        {actionError && <div className="alert alert-danger py-2">{actionError}</div>}
        <p>
          Archiving retires <strong>{licence.software_name}</strong>. It stays in history and in Total Purchased Licence Value,
          but no longer counts as active and becomes read-only.
        </p>
        {licence.assigned_seats > 0 && <p className="text-danger small">Unassign the {licence.assigned_seats} active seat(s) first.</p>}
      </Modal>
    </div>
  );
}
